import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

import { API } from "../../components/superadmin/users/constants";
import { useUserActions } from "../../components/superadmin/users/useUserActions";
import { UserFilters } from "../../components/superadmin/users/UserFilters";
import { UserTable } from "../../components/superadmin/users/UserTable";
import { SelfOrganizationCard } from "../../components/superadmin/users/SelfOrganizationCard";
import { ResetPasswordModal } from "../../components/superadmin/users/ResetPasswordModal";

export default function SuperAdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [rootEmail, setRootEmail] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");

  // True only for the immutable root account. The backend is the source of
  // truth (it returns root_superadmin_email on every list call), but we keep
  // a local boolean to gate UI affordances optimistically — actual mutations
  // are still 403'd server-side for non-root callers.
  const isRoot = !!(
    currentUser &&
    rootEmail &&
    currentUser.email?.trim().toLowerCase() === rootEmail.trim().toLowerCase()
  );

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (roleFilter) params.append("role", roleFilter);
      if (orgFilter) params.append("org_id", orgFilter);

      const res = await axios.get(`${API}/superadmin/users?${params}`, { withCredentials: true });
      setUsers(res.data.items);
      setRootEmail(res.data.root_superadmin_email || "");
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/organizations?limit=1000`, { withCredentials: true });
      setOrganizations(res.data.items || []);
    } catch (err) {
      console.error("Error loading organizations:", err);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, orgFilter]);

  const actions = useUserActions({ onMutated: fetchUsers });

  // Find the freshest copy of the current SA in the users list so that after
  // a self-link the card reflects the new org_name immediately (the
  // useAuth context isn't refetched on every action).
  const me = users.find((u) => u.user_id === currentUser?.user_id) || currentUser;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Usuarios</h1>
        <p className="text-slate-400 mt-1">Gestiona usuarios de todas las organizaciones</p>
      </div>

      <SelfOrganizationCard
        currentUser={me}
        organizations={organizations}
        onUpdated={fetchUsers}
      />

      <UserFilters
        search={search} setSearch={setSearch}
        orgFilter={orgFilter} setOrgFilter={setOrgFilter}
        roleFilter={roleFilter} setRoleFilter={setRoleFilter}
        organizations={organizations}
      />

      <UserTable
        users={users}
        loading={loading}
        isRoot={isRoot}
        organizations={organizations}
        actions={actions}
      />

      {/* SA-initiated password reset modal. Opens whenever
          `actions.resetPasswordTarget` is set by clicking the
          "Resetear contraseña" button on any row. */}
      <ResetPasswordModal
        user={actions.resetPasswordTarget}
        submitting={actions.resetting}
        onConfirm={actions.handleConfirmResetPassword}
        onClose={actions.handleCancelResetPassword}
      />
    </div>
  );
}
